import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SUPPORT_EMAIL } from '../config/contact';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  Schedule,
  PlayArrow,
  History,
  Upgrade,
  Business,
  CheckCircle,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ReviewsSection from './ReviewsSection';
import SubscriptionCard from './SubscriptionCard';
import FeedbackButton from './FeedbackButton';
import RecentProgress from './RecentProgress';
import HeroActionBar from './dashboard/HeroActionBar';
import SmartGuidance from './dashboard/SmartGuidance';
import RailInterviewReadiness from './dashboard/RailInterviewReadiness';
import CommunitySnapshot from './dashboard/CommunitySnapshot';

// Feature flag — set REACT_APP_DASHBOARD_V2=true to enable the new guided layout
const DASHBOARD_V2_ENABLED = process.env.REACT_APP_DASHBOARD_V2 === 'true';

const Dashboard = ({
  onStartInterview,
  onViewReviews,
  onPlanInterview,
  onStartSavedPlan,
  onNavigateToProgress,
  onNavigateToReplay,
  onNavigateToMarketplace,
  onNavigateToDiscover,
  onNavigateToFeed,
}) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInterviewDetailsDialog, setShowInterviewDetailsDialog] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState('');
  const [deletingPlanId, setDeletingPlanId] = useState(null);

  const { user, getDashboardData, getSubscriptionLimits } = useAuth();
  const subscriptionLimits = getSubscriptionLimits();

  useEffect(() => {
    console.log('🏠 Dashboard component mounted - user:', user);
    console.log('🏠 Dashboard component mounted - user role:', user?.role);
    console.log('🏠 Dashboard component mounted - is admin?:', user?.role === 'admin');
    console.log('🏠 Dashboard component mounted - token exists:', !!localStorage.getItem('token'));
    console.log('🏠 Dashboard component mounted - about to call loadDashboardData()');
    loadDashboardData();
    loadSavedPlans();
  }, []);

  // Add another useEffect to monitor user changes
  useEffect(() => {
    console.log('👤 User data changed:', user);
    console.log('👤 User role:', user?.role);
    console.log('👤 Is admin?:', user?.role === 'admin');
    console.log('👤 User email:', user?.email);
  }, [user]);

  const loadDashboardData = async () => {
    try {
      console.log('🔄 loadDashboardData() called - starting execution');
      setLoading(true);
      setError(''); // Clear any previous errors
      
      console.log('🔄 Loading dashboard data - about to call getDashboardData()...');
      const data = await getDashboardData();
      console.log('✅ Dashboard data received successfully:', data);
      
      setDashboardData(data);
    } catch (err) {
      console.error('Dashboard load error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        statusText: err.response?.statusText,
        config: err.config
      });
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load dashboard data';
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please check your permissions.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'success';
    if (score >= 6) return 'warning';
    return 'error';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewInterviewDetails = (interview) => {
    setSelectedInterview(interview);
    setShowInterviewDetailsDialog(true);
  };

  const loadSavedPlans = async () => {
    setPlansLoading(true);
    setPlansError('');
    try {
      const res = await axios.get('/api/plans', {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        params: { _ts: Date.now() }
      });
      if (res.data?.success) {
        setSavedPlans(Array.isArray(res.data.plans) ? res.data.plans : []);
      } else {
        setPlansError(res.data?.error || 'Failed to fetch saved plans');
      }
    } catch (err) {
      setPlansError(err.response?.data?.message || err.message || 'Failed to fetch saved plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const startSavedPlan = async (plan) => {
    if (!plan?.id) return;
    try {
      const res = await axios.post(`/api/plans/${plan.id}/start`);
      if (res.data?.success) {
        if (onStartSavedPlan) {
          onStartSavedPlan({
            planId: plan.id,
            sessionId: res.data.sessionId,
            question: res.data.question,
            ttsEnabled: true,
            duration: plan?.duration_minutes || 15,
            role: plan?.role,
            company: plan?.company
          });
        }
      } else {
        setPlansError(res.data?.error || 'Failed to start planned interview');
      }
    } catch (err) {
      setPlansError(err.response?.data?.message || err.message || 'Failed to start planned interview');
    }
  };

  const deleteSavedPlan = async (plan) => {
    if (!plan?.id) return;
    const confirmed = window.confirm('Delete this interview plan? This action cannot be undone.');
    if (!confirmed) return;
    setDeletingPlanId(plan.id);
    setPlansError('');
    try {
      const res = await axios.delete(`/api/plans/${plan.id}`);
      if (res.data?.success) {
        setSavedPlans(prev => prev.filter(p => p.id !== plan.id));
        // Re-fetch from server to ensure list matches backend state
        await loadSavedPlans();
      } else {
        setPlansError(res.data?.error || 'Failed to delete plan');
      }
    } catch (err) {
      setPlansError(err.response?.data?.message || err.message || 'Failed to delete plan');
    } finally {
      setDeletingPlanId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={loadDashboardData}
              startIcon={<Refresh />}
            >
              Retry
            </Button>
          }
        >
          <Typography variant="body1" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If this problem persists, please check the browser console for more details or contact {SUPPORT_EMAIL}.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const { stats, recentInterviews, progressMetrics } = dashboardData?.dashboard || { stats: {}, recentInterviews: [], progressMetrics: {} };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box
        sx={{
          // No side gutters / top padding on feed home — compose sits under the top bar
          px: { xs: 0, sm: 0 },
          pt: 0,
          pb: { xs: 2, sm: 2 },
          maxWidth: '100%',
          mx: 'auto',
          width: '100%',
          overflowX: 'clip',
        }}
      >
        {/* Mobile: LinkedIn/Meta one-line shortcuts, then feed */}
        {DASHBOARD_V2_ENABLED && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid="mobile-feed-shortcuts"
            sx={{
              display: { xs: 'flex', md: 'none' },
              mb: 1.5,
              pb: 0.25,
              px: 1.5,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <HeroActionBar
              onStartInterview={onStartInterview}
              onReviewLast={
                recentInterviews?.length > 0
                  ? () => onNavigateToReplay && onNavigateToReplay(recentInterviews[0]?.id || '')
                  : undefined
              }
              lastInterviewId={recentInterviews?.[0]?.id || null}
              variant="strip"
            />
            <SmartGuidance onViewProgress={onNavigateToProgress} variant="chip" />
          </Stack>
        )}

        {/* Desktop: feed-first; practice CTAs live in the right-rail Shortcuts */}
        <CommunitySnapshot
          onNavigateToDiscover={onNavigateToDiscover}
          onNavigateToFeed={onNavigateToFeed}
          onNavigateToMarketplace={onNavigateToMarketplace}
          onStartInterview={onStartInterview}
          onReviewLast={
            recentInterviews?.length > 0
              ? () => onNavigateToReplay && onNavigateToReplay(recentInterviews[0]?.id || '')
              : undefined
          }
          lastInterviewId={recentInterviews?.[0]?.id || null}
          afterPlacementSlot={
            DASHBOARD_V2_ENABLED ? (
              <Stack spacing={1.5} data-testid="dashboard-rail-progress-stack">
                <SmartGuidance onViewProgress={onNavigateToProgress} variant="rail" />
                <RailInterviewReadiness onViewProgress={onNavigateToProgress} />
              </Stack>
            ) : null
          }
        />

        {/* Below-feed sections keep side padding on xs; posts stay edge-to-edge */}
        <Box sx={{ px: { xs: 1.5, sm: 1 } }}>
        {/* Compact progress strip; full analytics expand on demand */}
        {dashboardData?.dashboard?.recentInterviews?.length > 0 && (
          <RecentProgress onViewProgress={onNavigateToProgress} />
        )}

        {/* ═══════ HISTORY & PLANS ═══════ */}
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1.2, fontWeight: 700, display: 'block', mb: 1.5 }}
        >
          History &amp; plans
        </Typography>

        {/* Saved Interview Plans */}
        <Card elevation={0} sx={{ mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <Schedule color="primary" fontSize="small" />
                Saved Interview Plans
              </Typography>
              <IconButton onClick={loadSavedPlans} size="small" aria-label="Refresh saved plans">
                <Refresh fontSize="small" />
              </IconButton>
            </Box>

            {plansError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {plansError}
              </Alert>
            )}

            {plansLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : savedPlans && savedPlans.length > 0 ? (
              <List>
                {savedPlans.map((p, index) => (
                  <React.Fragment key={p.id || index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Assessment color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={p.title || 'Untitled Plan'}
                        secondary={`${p.role || 'Role'}${p.company ? ' at ' + p.company : ''} • ${(Array.isArray(p.questions) ? p.questions.length : 0)} question(s) • ${p.difficulty || 'medium'} • ${p.status || 'draft'}`}
                        primaryTypographyProps={{
                          variant: 'subtitle1',
                          sx: { fontWeight: 'bold' }
                        }}
                        secondaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary'
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button 
                          size="small" 
                          variant="contained"
                          onClick={() => startSavedPlan(p)}
                        >
                          Start
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => deleteSavedPlan(p)}
                          disabled={deletingPlanId === (p.id)}
                        >
                          {deletingPlanId === (p.id) ? 'Deleting…' : 'Delete'}
                        </Button>
                      </Box>
                    </ListItem>
                    {index < savedPlans.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Schedule sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No saved plans yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Build a custom interview and save it to reuse later.
                </Typography>
                <Button variant="outlined" onClick={onPlanInterview}>
                  Build Custom Interview
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Progress Chart */}
        {progressMetrics?.averageScoreImprovement !== undefined && (
          <Card elevation={0} sx={{ mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <TrendingUp color="primary" fontSize="small" />
                Performance Improvement
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1">
                  Score Improvement:
                </Typography>
                <Chip 
                  label={`${progressMetrics.averageScoreImprovement >= 0 ? '+' : ''}${progressMetrics.averageScoreImprovement != null ? progressMetrics.averageScoreImprovement.toFixed(1) : '0.0'} points`}
                  color={progressMetrics.averageScoreImprovement >= 0 ? 'success' : 'error'}
                  icon={progressMetrics.averageScoreImprovement >= 0 ? <TrendingUp /> : <TrendingUp sx={{ transform: 'rotate(180deg)' }} />}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Comparing your last 5 interviews to previous 5 interviews
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Recent Interviews */}
        <Card elevation={0} sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <History color="primary" fontSize="small" />
                Recent Interviews
              </Typography>
              <IconButton onClick={loadDashboardData} size="small" aria-label="Refresh recent interviews">
                <Refresh fontSize="small" />
              </IconButton>
            </Box>

            {recentInterviews && recentInterviews.length > 0 ? (
              <List dense disablePadding>
                {recentInterviews.map((interview, index) => (
                  <React.Fragment key={interview.id}>
                    <ListItem sx={{ px: 0, py: 1, minHeight: 44 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Business color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${interview.role} at ${interview.company}`}
                        secondary={
                          <>
                            {formatDate(interview.startTime)}
                            {' · '}
                            {interview.totalQuestions} questions · {interview.status}
                          </>
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { fontWeight: 700 }
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary'
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        {interview.score && (
                          <Chip 
                            label={`${interview.score}/10`}
                            color={getScoreColor(interview.score)}
                            size="small"
                          />
                        )}
                        {interview.status === 'completed' && (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => handleViewInterviewDetails(interview)}
                          sx={{ textTransform: 'none' }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </ListItem>
                    {index < recentInterviews.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Assessment sx={{ fontSize: 48, color: 'grey.400', mb: 1.5 }} />
                <Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight={700}>
                  No interviews yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Start your first AI-powered interview to see your progress here.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<PlayArrow />}
                  onClick={onStartInterview}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Start Your First Interview
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ═══════ PLAN & USAGE ═══════ */}
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1.2, fontWeight: 700, display: 'block', mb: 1.5 }}
        >
          Plan &amp; usage
        </Typography>

        {/* Subscription Usage */}
        <Card elevation={0} sx={{ mb: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
              <Schedule color="primary" fontSize="small" />
              Monthly Usage
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Interviews this month
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {progressMetrics?.interviewsThisMonth || 0} / {subscriptionLimits.interviewsPerMonth === -1 ? '∞' : subscriptionLimits.interviewsPerMonth}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={subscriptionLimits.interviewsPerMonth === -1 ? 0 : 
                       Math.min(100, ((progressMetrics?.interviewsThisMonth || 0) / subscriptionLimits.interviewsPerMonth) * 100)}
                color={subscriptionLimits.interviewsPerMonth === -1 ? 'success' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            {user?.subscription === 'free' && (
              <Alert severity="info" action={
                <Button color="primary" size="small" variant="outlined" startIcon={<Upgrade />} sx={{ textTransform: 'none' }}>
                  Upgrade
                </Button>
              }>
                Upgrade to Premium for unlimited interviews and AI recommendations!
              </Alert>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mb: 2 }}>
          <SubscriptionCard />
        </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: '100%', mx: 'auto', width: '100%', px: { xs: 1.5, sm: 1 }, pb: 3 }}>
        <ReviewsSection 
          title="What Our Users Say"
          subtitle="See why thousands of job seekers trust JobMockAI"
          maxReviews={3}
          onViewAll={onViewReviews}
        />
      </Box>

            {/* Interview Details Dialog */}
      <Dialog 
        open={showInterviewDetailsDialog} 
        onClose={() => setShowInterviewDetailsDialog(false)} 
        maxWidth="md" 
        fullWidth
        aria-labelledby="interview-details-dialog-title"
      >
        <DialogTitle id="interview-details-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment color="primary" />
            Interview Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInterview && (
            <Grid container spacing={3}>
              {/* Interview Overview */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {selectedInterview.role} at {selectedInterview.company}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                           <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                             {selectedInterview.results?.overallScore || selectedInterview.score || 'N/A'}
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                             Overall Score
                           </Typography>
                         </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                           <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                             {selectedInterview.results?.totalQuestions || selectedInterview.totalQuestions || 0}
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                             Questions
                           </Typography>
                         </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                            {selectedInterview.status}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                           <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                             {selectedInterview.results?.duration || selectedInterview.duration || 'N/A'}
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                             Duration (min)
                           </Typography>
                         </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Started:</strong> {formatDate(selectedInterview.startTime)}
                      </Typography>
                      {selectedInterview.endTime && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Completed:</strong> {formatDate(selectedInterview.endTime)}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Interview Results */}
              {selectedInterview.results && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Interview Results
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        <strong>Time Spent:</strong> {Math.round((selectedInterview.results.timeSpent || 0) / 60)} minutes
                      </Typography>
                      
                      {selectedInterview.results.responses && selectedInterview.results.responses.length > 0 && (
                        <Box>
                          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                            Questions & Answers
                          </Typography>
                          {selectedInterview.results.responses.map((response, index) => (
                            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
                                    Question {index + 1}
                                  </Typography>
                                  {response.score && (
                                    <Chip 
                                      label={`${response.score}/10`}
                                      color={getScoreColor(response.score)}
                                      size="small"
                                    />
                                  )}
                                </Box>
                                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                                  {response.question?.text || response.question}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Your Answer:</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2, borderLeft: '3px solid #E2E8F0' }}>
                                  {response.answer}
                                </Typography>
                                {response.feedback && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                      <strong>Feedback:</strong>
                                    </Typography>
                                    {response.feedback.strengths && response.feedback.strengths.length > 0 && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                                          Strengths:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {response.feedback.strengths.join(', ')}
                                        </Typography>
                                      </Box>
                                    )}
                                    {response.feedback.improvements && response.feedback.improvements.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                                          Areas for Improvement:
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {response.feedback.improvements.join(', ')}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInterviewDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Feedback Button */}
      <FeedbackButton position="bottom-right" />
    </Box>
  );
};

export default Dashboard;